import { Capacitor } from "@capacitor/core";
import { isTauri as detectTauri } from "@tauri-apps/api/core";
import * as tauriOs from "@tauri-apps/plugin-os";

type UserAgentDataLike = {
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<{
    platformVersion?: string;
  }>;
};

type BrowserNavigator = Navigator & {
  userAgentData?: UserAgentDataLike;
  standalone?: boolean;
};

type TauriAwareWindow = Window & {
  chrome?: {
    webview?: unknown;
  };
  __TAURI_INTERNALS__?: {
    invoke?: unknown;
  };
};

export type RisuEnvironmentLabel = "local" | "node" | "web" | "web(dev)";

const browserNavigator =
  typeof navigator !== "undefined"
    ? (navigator as BrowserNavigator)
    : ({} as BrowserNavigator);

const isWindowsWebViewHost =
  typeof window !== "undefined" &&
  Boolean((window as TauriAwareWindow).chrome?.webview);

export const isTauri: boolean = detectTauri() || isWindowsWebViewHost;

function getTauriOsTypeSafe(): string | null {
  if (!isTauri) return null;
  try {
    return tauriOs.type();
  } catch {
    const osName = getBrowserOSName();
    if (osName === "Windows") return "windows";
    if (osName === "macOS") return "macos";
    if (osName === "Linux") return "linux";
    return null;
  }
}

const tauriOsType = getTauriOsTypeSafe();
export const isTauriMacOS: boolean = tauriOsType === "macos";
export const isTauriWindows: boolean = tauriOsType === "windows";
export const isTauriLinux: boolean = tauriOsType === "linux";
export const isCapacitor: boolean = !isTauri && Capacitor.isNativePlatform();
export const isCapacitorAndroid: boolean =
  isCapacitor && Capacitor.getPlatform() === "android";
export const isNodeServer: boolean = !!(
  globalThis as typeof globalThis & { __NODE__?: boolean }
).__NODE__;
export const isWeb: boolean =
  typeof location !== "undefined" &&
  !isTauri &&
  !isCapacitor &&
  !isNodeServer &&
  location.hostname === "risuai.xyz";
export const isMobile: boolean =
  typeof browserNavigator.userAgent === "string" &&
  /Android|iPhone|iPad|iPod|webOS/i.test(browserNavigator.userAgent);

export const isFirefox: boolean =
  typeof browserNavigator.userAgent === "string" &&
  browserNavigator.userAgent.includes("Firefox");

export function isTauriRuntimeReady(): boolean {
  if (!isTauri) return true;
  if (typeof window === "undefined") return false;
  const internals = (window as TauriAwareWindow).__TAURI_INTERNALS__;
  return typeof internals?.invoke === "function";
}

export async function waitForTauriRuntimeReady(
  timeoutMs = 2_000,
): Promise<boolean> {
  if (isTauriRuntimeReady()) return true;
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 10));
    if (isTauriRuntimeReady()) return true;
  }
  return false;
}

function normalizeOSVersion(version?: string | null): string | null {
  if (!version) {
    return null;
  }

  const normalized = version.trim().replace(/_/g, ".");
  return normalized && normalized !== "0.0.0" ? normalized : null;
}

function formatOSName(osName: string): string {
  switch (osName.toLowerCase()) {
    case "macos":
      return "macOS";
    case "ios":
      return "iOS";
    case "windows":
      return "Windows";
    case "android":
      return "Android";
    case "linux":
      return "Linux";
    case "chromeos":
      return "ChromeOS";
    default:
      return osName;
  }
}

function joinOSLabel(osName: string, version: string | null): string {
  return version ? `${osName} ${version}` : osName;
}

export function isIOS(): boolean {
  const ua = browserNavigator.userAgent || "";
  const isAppleMobile = /iPad|iPhone|iPod/.test(ua);

  // iPadOS 13+
  const isIpadOS =
    browserNavigator.platform === "MacIntel" &&
    browserNavigator.maxTouchPoints > 1;

  return isAppleMobile || isIpadOS;
}

function getBrowserOSName(): string {
  const uaPlatform = browserNavigator.userAgentData?.platform;
  if (uaPlatform) {
    return formatOSName(uaPlatform);
  }

  const ua = browserNavigator.userAgent || "";
  const platform = browserNavigator.platform || "";

  if (/Windows/i.test(ua)) return "Windows";
  if (/Android/i.test(ua)) return "Android";
  if (isIOS()) return "iOS";
  if (/CrOS/i.test(ua)) return "ChromeOS";
  if (/Mac/i.test(platform) || /Mac OS X/i.test(ua)) return "macOS";
  if (/Linux/i.test(platform) || /Linux/i.test(ua)) return "Linux";

  return platform || "Unknown";
}

function getUserAgentOSVersion(osName: string): string | null {
  const ua = browserNavigator.userAgent || "";

  switch (osName) {
    case "Android":
      return normalizeOSVersion(ua.match(/Android ([\d.]+)/i)?.[1]);
    case "iOS":
      return normalizeOSVersion(
        ua.match(/(?:CPU (?:iPhone )?OS|iPhone OS|CPU OS) ([\d_]+)/i)?.[1],
      );
    case "ChromeOS":
      return normalizeOSVersion(ua.match(/CrOS [^ ]+ ([\d.]+)/i)?.[1]);
    case "Windows": {
      const windowsVersion = normalizeOSVersion(
        ua.match(/Windows NT ([\d.]+)/i)?.[1],
      );
      return windowsVersion ? `NT ${windowsVersion}` : null;
    }
    default:
      return null;
  }
}

async function getBrowserHighEntropyOSVersion(): Promise<string | null> {
  try {
    const values = await browserNavigator.userAgentData?.getHighEntropyValues?.(
      ["platformVersion"],
    );
    return normalizeOSVersion(values?.platformVersion);
  } catch (error) {
    console.warn("Failed to read browser platformVersion:", error);
    return null;
  }
}

export function getRisuEnvironmentLabel(): RisuEnvironmentLabel {
  if (isTauri || isCapacitor) {
    return "local";
  }

  if (import.meta.env.DEV) {
    return "web(dev)";
  }

  if (isNodeServer) {
    return "node";
  }

  return "web";
}

export function getFallbackOSLabel(): string {
  const osName = getBrowserOSName();
  return joinOSLabel(osName, getUserAgentOSVersion(osName));
}

export async function getDetailedOSLabel(): Promise<string> {
  if (isTauri) {
    try {
      return joinOSLabel(
        formatOSName(tauriOs.type()),
        normalizeOSVersion(tauriOs.version()),
      );
    } catch (error) {
      console.warn("Failed to read native OS details:", error);
    }
  }

  const osName = getBrowserOSName();
  const browserVersion = await getBrowserHighEntropyOSVersion();

  return joinOSLabel(osName, browserVersion ?? getUserAgentOSVersion(osName));
}

export const isInStandaloneMode: boolean =
  typeof window !== "undefined"
    ? window.matchMedia?.("(display-mode: standalone)")?.matches ||
      !!browserNavigator.standalone ||
      (typeof document !== "undefined" &&
        Boolean(document.referrer?.includes("android-app://")))
    : false;
